import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, ROLE_SI_ADMIN } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { MODELOS_MENSAGEM_PADRAO } from '../../models/mensagensContato.modelos';
import type { IdModeloMensagem } from '../../models/mensagensContato.modelos';
import { mensagensContatoService } from '../../services/mensagensContatoService';
import type { ModeloMensagemExtra, ModeloMensagemSalvo } from '../../services/mensagensContatoService';

const CHAVE = ['si', 'mensagens-contato'] as const;

export interface ModeloMensagemEfetivo {
  id: IdModeloMensagem;
  titulo: string;
  quando: string;
  textoPadrao: string;
  texto: string;
  documentoIds: string[];
  personalizado: boolean;
}

/** Modelos em uso: o editado pelo SI ou, sem edição, o padrão — mais os modelos criados pelo
 * SI (`extras`). Só o SI lê (si_config_mensagens). */
export function useModelosMensagem() {
  const { userHasRole } = useAuth();
  const consulta = useQuery({
    queryKey: [...CHAVE, 'modelos'],
    queryFn: () => mensagensContatoService.listarSalvos(),
    enabled: userHasRole(ROLE_SI_ADMIN),
    staleTime: 5 * 60 * 1000,
  });
  const modelos = useMemo<ModeloMensagemEfetivo[]>(
    () =>
      MODELOS_MENSAGEM_PADRAO.map((padrao) => {
        const salvo = consulta.data?.padroes.find((s) => s.id === padrao.id);
        return {
          id: padrao.id,
          titulo: padrao.titulo,
          quando: padrao.quando,
          textoPadrao: padrao.texto,
          texto: salvo?.texto ?? padrao.texto,
          documentoIds: salvo?.documentoIds ?? [],
          personalizado: Boolean(salvo),
        };
      }),
    [consulta.data]
  );
  return { ...consulta, modelos, extras: consulta.data?.extras ?? [] };
}

export function useDocumentosParaMensagem() {
  const { userHasRole } = useAuth();
  return useQuery({
    queryKey: [...CHAVE, 'documentos'],
    queryFn: () => mensagensContatoService.listarDocumentos(),
    enabled: userHasRole(ROLE_SI_ADMIN),
    staleTime: 5 * 60 * 1000,
  });
}

export function useModeloMensagemMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const invalidar = () => queryClient.invalidateQueries({ queryKey: CHAVE });
  const por = { id: user?.collaboratorId ?? user?.uid ?? '', name: user?.displayName ?? user?.email ?? '' };
  return {
    salvar: useMutation({
      mutationFn: (modelo: ModeloMensagemSalvo) => mensagensContatoService.salvar(modelo, por),
      onSuccess: () => {
        toast.success('Modelo salvo — a próxima mensagem já sai com o texto novo.');
        invalidar();
      },
      onError: () => toast.error('Não foi possível salvar o modelo. Tente de novo.'),
    }),
    salvarExtra: useMutation({
      mutationFn: (modelo: Omit<ModeloMensagemExtra, 'id'> & { id?: string }) =>
        mensagensContatoService.salvarExtra(modelo, por),
      onSuccess: () => {
        toast.success('Modelo salvo — já aparece no seletor "Mensagem" das solicitações.');
        invalidar();
      },
      onError: () => toast.error('Não foi possível salvar o modelo. Tente de novo.'),
    }),
    excluirExtra: useMutation({
      mutationFn: (id: string) => mensagensContatoService.restaurar(id),
      onSuccess: () => {
        toast.success('Modelo excluído.');
        invalidar();
      },
      onError: () => toast.error('Não foi possível excluir. Tente de novo.'),
    }),
    restaurar: useMutation({
      mutationFn: (id: IdModeloMensagem) => mensagensContatoService.restaurar(id),
      onSuccess: () => {
        toast.success('Texto padrão restaurado.');
        invalidar();
      },
      onError: () => toast.error('Não foi possível restaurar. Tente de novo.'),
    }),
  };
}
